import { IIntervalDraft } from '~/client/src/shared/components/IntervalEditor'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore, {
  MILLISECONDS_IN_MINUTE,
} from '~/client/src/shared/stores/ui/ProjectDate.store'
import IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'
import { withResolvedOperatingMode } from '~/client/src/shared/utils/operatingIntervalMode'
import { resolveOperatingEditorSeed } from '~/client/src/shared/utils/operatingIntervals'
import { doesWindowEndNextDay } from '~/client/src/shared/utils/projectSchedule'

const FIRST_WEEKDAY = 1
const LAST_WEEKDAY = 7

// Today at the given project-local hour/minute. Anchored on the project's clock, so
// the instant means the same wall-clock time to every viewer and reads back as those
// hours everywhere — the chips, the editor and the booking validator all resolve it
// in project time.
function todayAt(
  projectDateStore: ProjectDateStore,
  hourMinute: number[],
): number {
  const [hour, minute] = hourMinute
  return projectDateStore.setHours(new Date(), hour, minute).getTime()
}

// The operating window a resource falls back to while it has no daily window of its
// own: a daily interval spanning the project working hours. Also seeds the editor's
// "add interval" form. Matches what the booking validator enforces — it builds the
// same fallback from projectWorkingHours (see getHoursOfOperationMessages).
export function buildInheritedOperatingInterval(
  projectDateStore: ProjectDateStore,
): IMsDateInterval {
  return {
    startDate: todayAt(
      projectDateStore,
      projectDateStore.projectStartHourMinutes,
    ),
    endDate: todayAt(projectDateStore, projectDateStore.projectEndHourMinutes),
    isDaily: true,
  }
}

/** A window some run of the week works, as the label grouping reads it. */
interface IWeekdayWindow {
  dayOfWeek: number
  startDate: number
  endDate: number
}

/** One line of a schedule chip: its text, and whether its window ends tomorrow. */
export interface IScheduleRow {
  label: string
  endsNextDay: boolean
}

interface IWeekdayRun extends IWeekdayWindow {
  /** Last day of the run; equal to `dayOfWeek` for a single day. */
  to: number
}

/**
 * Folds a week into runs of consecutive days that work the same window.
 *
 * A gap breaks a run: Monday and Wednesday on the same hours are two runs, not
 * "Mon–Wed", which would claim a Tuesday nobody works.
 */
function groupWeekdayRuns(
  projectDateStore: ProjectDateStore,
  windows: IWeekdayWindow[],
): IWeekdayRun[] {
  const { getDaySeconds } = projectDateStore
  const runs: IWeekdayRun[] = []

  ;[...windows]
    .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
    .forEach(window => {
      const previous = runs[runs.length - 1]
      const continuesRun =
        previous &&
        previous.to === window.dayOfWeek - 1 &&
        getDaySeconds(previous.startDate) === getDaySeconds(window.startDate) &&
        getDaySeconds(previous.endDate) === getDaySeconds(window.endDate)

      if (continuesRun) {
        previous.to = window.dayOfWeek
        return
      }

      runs.push({ ...window, to: window.dayOfWeek })
    })

  return runs
}

/**
 * A whole week as one line per run of days that work the same window.
 *
 * The schedule is a single thing — one chip, opening the whole week — so these
 * are the lines of that chip rather than chips of their own. Each run states its
 * own window, since the runs that need separate lines are usually the ones whose
 * hours differ.
 */
export function buildWeekdayScheduleRows(
  projectDateStore: ProjectDateStore,
  windows: IWeekdayWindow[],
): IScheduleRow[] {
  const { weekdayNamesList, getTimeToDisplay, getDaySeconds } = projectDateStore
  const dayName = (dayOfWeek: number) => weekdayNamesList[dayOfWeek - 1]

  return groupWeekdayRuns(projectDateStore, windows).map(run => {
    // An end at or before the start is the overnight window, not a backwards
    // one. Carried as a flag rather than spelled into the text so the chip can
    // mark it the way the editor's field does, in muted type.
    const endsNextDay = doesWindowEndNextDay(
      getDaySeconds(run.startDate),
      getDaySeconds(run.endDate),
    )
    const times = `${getTimeToDisplay(run.startDate)} – ${getTimeToDisplay(
      run.endDate,
    )}`

    // A run covering every day is the every-day case, and reads better as such
    // than as "Sun–Sat".
    const days =
      run.dayOfWeek === FIRST_WEEKDAY && run.to === LAST_WEEKDAY
        ? Localization.translator.daily
        : run.dayOfWeek === run.to
          ? dayName(run.dayOfWeek)
          : `${dayName(run.dayOfWeek)}–${dayName(run.to)}`

    return { label: `${days}, ${times}`, endsNextDay }
  })
}

export function buildInheritedHoursRows(
  projectDateStore: ProjectDateStore,
): IScheduleRow[] {
  const { getHoursMinutes } = projectDateStore

  return buildWeekdayScheduleRows(
    projectDateStore,
    projectDateStore.workingWeekdayNumbers.map(dayOfWeek => {
      const { startTime, endTime } =
        projectDateStore.getWorkingHoursForWeekday(dayOfWeek)

      return {
        dayOfWeek,
        startDate: todayAt(projectDateStore, getHoursMinutes(startTime)),
        endDate: todayAt(projectDateStore, getHoursMinutes(endTime)),
      }
    }),
  )
}

// What the operating-hours editor opens on, for both the per-resource cell and the
// bulk editor. `editIndex` is null when adding. A daily window carries only a time of
// day — its stored calendar date is whenever it was last saved — so it is re-based
// onto today, otherwise switching the seed to the Date tab pre-fills a stale, often
// past, date.
export function buildOperatingIntervalSeed(
  intervals: IMsDateInterval[],
  editIndex: number | null,
  projectDateStore: ProjectDateStore,
): IIntervalDraft {
  const seed = resolveOperatingEditorSeed(intervals, editIndex)
  if (!seed) return buildInheritedOperatingInterval(projectDateStore)

  const resolved = withResolvedOperatingMode(seed)
  if (!resolved.isDaily) return resolved

  const timeOfDay = (timestamp: number) =>
    todayAt(projectDateStore, projectDateStore.getHoursMinutes(timestamp))
  return {
    ...resolved,
    startDate: timeOfDay(resolved.startDate),
    endDate:
      resolved.endDate == null ? resolved.endDate : timeOfDay(resolved.endDate),
  }
}

export function buildDefaultClosure(
  projectDateStore: ProjectDateStore,
): IIntervalDraft {
  const startDate = Date.now()
  const workingEnd = todayAt(
    projectDateStore,
    projectDateStore.projectEndHourMinutes,
  )
  return {
    startDate,
    // Past the working-end hour, the end would land before the start; keep it
    // one minute after, matching the IntervalEditor's ordering guard.
    endDate:
      workingEnd <= startDate ? startDate + MILLISECONDS_IN_MINUTE : workingEnd,
    isDaily: false,
  }
}
